import PropTypes from 'prop-types';
import React, { memo } from 'react';
import './styles.scss';

const WhatsAppTemplatePreview = memo(({ header, headerType, mediaUrl, body, footer, buttons }) => {
  const renderHeader = () => {
    switch (headerType) {
      case 'IMAGE':
        return (
          <div className="header-media">
            <img src={mediaUrl || 'placeholder-image.png'} alt="Header" className="img-fluid" />
          </div>
        );
      case 'VIDEO':
        return (
          <div className="header-media">
            <video src={mediaUrl} controls className="img-fluid">
              Your browser does not support the video tag.
            </video>
          </div>
        );
      case 'DOCUMENT':
        return (
          <div className="header-document">
            <i className="fas fa-file-alt"></i>
            <span>{header}</span>
          </div>
        );
      default:
        return header && <h5 className="header">{header}</h5>;
    }
  };

  const renderButton = (button, index) => {
    const buttonIcon = {
      QUICK_REPLY: 'fa-reply',
      URL: 'fa-external-link-alt',
      PHONE_NUMBER: 'fa-phone',
    }[button.type];

    return (
      <button
        key={index}
        className="template-button"
        title={
          button.type === 'URL'
            ? button.url
            : button.type === 'PHONE_NUMBER'
            ? button.phoneNumber
            : ''
        }
      >
        <i className={`fas ${buttonIcon}`}></i>
        {button.text}
      </button>
    );
  };

  const parseWhatsAppFormat = (text) => {
    if (!text) return text;

    // Split the text into segments that should be formatted differently
    const segments = [];
    let currentIndex = 0;

    // Regular expression to match WhatsApp formatting
    const formatRegex = /(\*[^*]+\*)|(_[^_]+_)|(~[^~]+~)/g;
    let match;

    while ((match = formatRegex.exec(text)) !== null) {
      // Add unformatted text before the match
      if (match.index > currentIndex) {
        segments.push({
          text: text.slice(currentIndex, match.index),
          format: 'none',
        });
      }

      // Determine format type and add formatted text
      const [fullMatch] = match;
      const format = fullMatch.startsWith('*')
        ? 'bold'
        : fullMatch.startsWith('_')
        ? 'italic'
        : 'strikethrough';

      segments.push({
        text: fullMatch.slice(1, -1),
        format,
      });

      currentIndex = match.index + fullMatch.length;
    }

    // Add any remaining unformatted text
    if (currentIndex < text.length) {
      segments.push({
        text: text.slice(currentIndex),
        format: 'none',
      });
    }

    // Render segments with appropriate formatting
    return segments.map((segment, index) => {
      switch (segment.format) {
        case 'bold':
          return <strong key={index}>{segment.text}</strong>;
        case 'italic':
          return <i key={index}>{segment.text}</i>;
        case 'strikethrough':
          return <s key={index}>{segment.text}</s>;
        default:
          return <span key={index}>{segment.text}</span>;
      }
    });
  };

  return (
    <div className="wa-template-preview">
      <div className="card border-0">
        <div className="card-header bg-light border-0">
          <h5 className="mb-0">Template Preview</h5>
        </div>
        <div className="card-body">
          <div className="message-container">
            <div className="message-bubble">
              {renderHeader()}

              <div className="message-content">
                {body ? (
                  body.split('\n').map((text, index) => (
                    <p key={index} className="body-text">
                      {parseWhatsAppFormat(text)}
                    </p>
                  ))
                ) : (
                  <p className="body-text empty-body">No message content</p>
                )}

                {footer && <p className="footer">{footer}</p>}

                <div className="timestamp">
                  {new Date().toLocaleTimeString('en-US', {
                    hour: 'numeric',
                    minute: '2-digit',
                    hour12: true,
                  })}
                </div>
              </div>

              {buttons?.length > 0 && (
                <div className="button-container">
                  {buttons.map((button, index) => renderButton(button, index))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

WhatsAppTemplatePreview.propTypes = {
  header: PropTypes.string,
  headerType: PropTypes.oneOf(['TEXT', 'IMAGE', 'VIDEO', 'DOCUMENT']),
  mediaUrl: PropTypes.string,
  body: PropTypes.string,
  footer: PropTypes.string,
  buttons: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.oneOf(['QUICK_REPLY', 'URL', 'PHONE_NUMBER']).isRequired,
      text: PropTypes.string.isRequired,
      url: PropTypes.string,
      phoneNumber: PropTypes.string,
    })
  ),
};

WhatsAppTemplatePreview.displayName = 'WhatsAppTemplatePreview';

export default WhatsAppTemplatePreview;
